// VOC Feedback Widget — REALM catalog rating popup

import React, { useState, useEffect, useCallback, useRef } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Box from "@amzn/meridian/box";
import { useAccessContext } from "src/context/AuthenticationContext";
import Button from "@amzn/meridian/button";
import Text from "@amzn/meridian/text";
import Heading from "@amzn/meridian/heading";
import Textarea from "@amzn/meridian/textarea";
import Alert from "@amzn/meridian/alert";
import Icon from "@amzn/meridian/icon";
import Link from "@amzn/meridian/link";
import Loader from "@amzn/meridian/loader";
import starFilledTokens from "@amzn/meridian-tokens/base/icon/star-filled";
import starTokens from "@amzn/meridian-tokens/base/icon/star";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import { getCatalogTools, putCatalogRating, CatalogTool, ensureImpactMidwayAuth } from "src/common/api/impact.api";
import {
    EAGLE_EYE_TOOL_NAME,
    FEEDBACK_AUTO_OPEN_DELAY_MS,
    FEEDBACK_AUTO_CLOSE_DELAY_MS,
    isFeedbackDismissed,
    dismissFeedback,
    dismissFeedbackPermanent,
} from "src/utils/feedback-config";
import "./feedback-modal.component.scss";

interface FeedbackModalProps {
    externalOpen: number;
}

const FeedbackModalComponent = ({ externalOpen }: FeedbackModalProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [rating, setRating] = useState(0);
    const [hoveredStar, setHoveredStar] = useState(0);
    const [feedbackText, setFeedbackText] = useState("");
    const [ratingError, setRatingError] = useState("");
    const [feedbackError, setFeedbackError] = useState("");
    const [submitError, setSubmitError] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [impactToolId, setImpactToolId] = useState<string | null>(null);
    const [toolFetchError, setToolFetchError] = useState(false);

    const autoOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Auto-open after delay on first visit (only if user has access)
    const hasAccess = useAccessContext();
    useEffect(() => {
        if (!isFeedbackDismissed() && hasAccess === true) {
            autoOpenTimerRef.current = setTimeout(() => setIsOpen(true), FEEDBACK_AUTO_OPEN_DELAY_MS);
        }
        return () => { if (autoOpenTimerRef.current) clearTimeout(autoOpenTimerRef.current); };
    }, [hasAccess]);

    // Open when "Rate Us" is clicked
    useEffect(() => {
        if (externalOpen > 0) {
            setRating(0);
            setHoveredStar(0);
            setFeedbackText("");
            setRatingError("");
            setFeedbackError("");
            setSubmitError("");
            setSubmitted(false);
            setToolFetchError(false);
            setImpactToolId(null);
            setIsOpen(true);
        }
    }, [externalOpen]);

    // Fetch EagleEye's toolId from IMPACT when popup opens
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setToolFetchError(false);
        ensureImpactMidwayAuth()
            .then(() => getCatalogTools())
            .then((response) => {
                if (cancelled) return;
                const tools: CatalogTool[] = response.data;
                const eagleEyeTool = tools.find(
                    (t) => t.name.toUpperCase() === EAGLE_EYE_TOOL_NAME.toUpperCase()
                );
                if (eagleEyeTool) {
                    setImpactToolId(eagleEyeTool.id);
                } else {
                    console.warn("[IMPACT] EagleEye not found in catalog tools");
                    setToolFetchError(true);
                }
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("[IMPACT] getCatalogTools failed:", err);
                setToolFetchError(true);
            });
        return () => { cancelled = true; };
    }, [isOpen, externalOpen]);

    const handleClose = useCallback(() => {
        dismissFeedback();
        setIsOpen(false);
    }, []);

    // Auto-close success screen
    useEffect(() => {
        if (submitted) {
            autoCloseTimerRef.current = setTimeout(() => handleClose(), FEEDBACK_AUTO_CLOSE_DELAY_MS);
        }
        return () => { if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current); };
    }, [submitted, handleClose]);

    // Global Escape key listener
    useEffect(() => {
        if (!isOpen) return;
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") handleClose();
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen, handleClose]);

    const handleStarClick = useCallback((starNumber: number) => {
        setRating(starNumber);
        setRatingError("");
    }, []);

    const handleReset = useCallback(() => {
        setRating(0);
        setHoveredStar(0);
        setFeedbackText("");
        setRatingError("");
        setFeedbackError("");
        setSubmitError("");
    }, []);

    const handleSubmit = useCallback(async () => {
        if (rating === 0) { setRatingError("Please select a rating."); return; }
        if (!feedbackText.trim()) { setFeedbackError("Please provide your feedback."); return; }
        if (!impactToolId) { setSubmitError("Unable to submit feedback. Please try again later."); return; }

        setSubmitting(true);
        setSubmitError("");

        try {
            await putCatalogRating({ toolId: impactToolId, score: rating, comment: feedbackText.trim() });
            dismissFeedbackPermanent();
            setSubmitted(true);
        } catch {
            setSubmitError("Unable to submit feedback. Please try again later.");
        } finally {
            setSubmitting(false);
        }
    }, [rating, feedbackText, impactToolId]);

    if (!isOpen) return null;

    if (submitted) {
        return (
            <div
                className="feedback-popup-container"
                role="dialog"
                aria-label="Submit feedback"
            >
                <Column type="outline" spacingInset="none" className="feedback-popup">
                    <Box backgroundColor="secondary" spacingInset="300 400">
                        <Row alignmentHorizontal="justify" alignmentVertical="center">
                            <Heading level={5}>Submit feedback</Heading>
                            <Button type="icon" size="small" onClick={handleClose} label="Close feedback">
                                <Icon tokens={closeSmallTokens} />
                            </Button>
                        </Row>
                    </Box>
                    <Column spacingInset="300 400" spacing="400">
                        <Alert type="success">
                            <Text>
                                <b>Thank you for your feedback!</b><br />
                                Your response has been recorded.
                            </Text>
                        </Alert>
                        <Row alignmentHorizontal="end">
                            <Button type="secondary" size="small" onClick={handleClose}>Close</Button>
                        </Row>
                    </Column>
                </Column>
            </div>
        );
    }

    return (
        <div
            className="feedback-popup-container"
            role="dialog"
            aria-label="Submit feedback"
        >
            <Column type="outline" spacingInset="none" className="feedback-popup">
                <Box backgroundColor="secondary" spacingInset="300 400">
                    <Row alignmentHorizontal="justify" alignmentVertical="center">
                        <Heading level={5}>Submit feedback</Heading>
                        <Button type="icon" size="small" onClick={handleClose} label="Close feedback">
                            <Icon tokens={closeSmallTokens} />
                        </Button>
                    </Row>
                </Box>
                <Column spacingInset="300 400" spacing="400">
                    <Text>
                        Share your experience with EagleEye. Your rating will help us improve
                        and will reflect on the{" "}
                        <Link href="https://impact.tools.amazon.dev/realmhub?tab=Catalog" target="_blank" rel="noreferrer">
                            REALM App Catalog
                        </Link>.
                    </Text>

                    {toolFetchError && (
                        <Alert type="error">Unable to load feedback form. Please try again later.</Alert>
                    )}

                    <Column spacing="xsmall">
                        <Text><b>Overall rating</b></Text>
                        <Text type="b200" color="secondary">Choose your rating 1 to 5</Text>
                        <Row spacing="none">
                            {[1, 2, 3, 4, 5].map((star) => {
                                const isFilled = star <= (hoveredStar || rating);
                                return (
                                    <Button
                                        key={star}
                                        type="icon"
                                        size="small"
                                        onClick={() => handleStarClick(star)}
                                        onMouseEnter={() => setHoveredStar(star)}
                                        onMouseLeave={() => setHoveredStar(0)}
                                        label={`Rate ${star} out of 5`}
                                    >
                                        <Icon
                                            tokens={isFilled ? starFilledTokens : starTokens}
                                            className={isFilled ? "feedback-star-filled" : "feedback-star-empty"}
                                        />
                                    </Button>
                                );
                            })}
                        </Row>
                        {ratingError && <Alert type="error">{ratingError}</Alert>}
                    </Column>

                    <Column spacing="xsmall">
                        <Text><b>Your feedback</b></Text>
                        <Textarea
                            value={feedbackText}
                            onChange={(val: string) => {
                                setFeedbackText(val);
                                setFeedbackError("");
                            }}
                            placeholder="Tell us what worked well or what we can improve..."
                            rows={4}
                        />
                        {feedbackError && <Alert type="error">{feedbackError}</Alert>}
                    </Column>

                    {submitError && <Alert type="error">{submitError}</Alert>}

                    <Row spacing="300">
                        <Button type="tertiary" size="small" onClick={handleReset} disabled={submitting}>
                            Reset
                        </Button>
                        <Button type="primary" size="small" onClick={handleSubmit} disabled={submitting || toolFetchError || !impactToolId}>
                            {submitting ? <Loader size="small" /> : "Submit feedback"}
                        </Button>
                    </Row>
                </Column>
            </Column>
        </div>
    );
};

export default FeedbackModalComponent;
